import { create } from 'zustand';

/** Selectable look-ahead horizons for predicted-trajectory vectors (minutes) */
export const LOOKAHEAD_OPTIONS = [1, 3, 5] as const;

/** A supported look-ahead value */
export type LookaheadMinutes = (typeof LOOKAHEAD_OPTIONS)[number];

interface TrajectoryState {
  /** How far ahead to project, in minutes */
  lookaheadMinutes: LookaheadMinutes;
  /** Layer ids with trajectory vectors switched on */
  enabledLayers: Set<string>;
  /** Set the look-ahead horizon */
  setLookahead: (minutes: LookaheadMinutes) => void;
  /** Toggle trajectory vectors for a layer */
  toggleLayer: (layerId: string) => void;
  /** Whether a layer has trajectories enabled */
  isEnabled: (layerId: string) => boolean;
}

/** Store controlling predicted-trajectory (velocity-vector) rendering. */
export const useTrajectoryStore = create<TrajectoryState>((set, get) => ({
  lookaheadMinutes: 3,
  enabledLayers: new Set<string>(),
  setLookahead: (minutes) => set({ lookaheadMinutes: minutes }),
  toggleLayer: (layerId) =>
    set((state) => {
      const next = new Set(state.enabledLayers);
      if (next.has(layerId)) {
        next.delete(layerId);
      } else {
        next.add(layerId);
      }
      return { enabledLayers: next };
    }),
  isEnabled: (layerId) => get().enabledLayers.has(layerId),
}));
